// react
import { useContext, useEffect, useRef, useState } from 'react';

// types
import { LoadingStatus, TourConfig } from '../../../@types';

// context
import { ContextUser } from '../../../App.context';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useLocation, useSearchParams } from 'react-router-dom';

// mui
import { alpha } from '@mui/material';

// theme
import { theme } from '../../../theme';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// ui
import { Popover, PopoverProps } from '../Popover/Popover';

// config
import { getTourConfigs } from './Tours.config';

export const Tours = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');

	// context
	const { user } = useContext(ContextUser);

	// state
	const [tourStatus, setTourStatus] = useState<LoadingStatus>('DEFAULT');
	const [stepIndex, setStepIndex] = useState(0);
	const [tourActive, setTourActive] = useState<TourConfig | null>(null);
	const [tours, setTours] = useState<TourConfig[]>([]);

	// refs
	const refActivatedTours = useRef(new Set<string>());

	// vars
	const isTourClosing = tourStatus === 'CLOSING';
	const tourConfigs = getTourConfigs({ t });

	// Find first matching tour for current route and user role
	let tourMatching = null;

	if (user) {
		const tourFound = tourConfigs.find(
			(tour) =>
				tour.isActive && location.pathname.startsWith(tour.route) && tour.roles.includes(user.role)
		);

		if (tourFound) {
			tourMatching = tourFound;
		}
	}

	// Check if the matching tour has been completed by this user
	const { isLoading: isLoadingTours } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/tours?name=${tourMatching?.name}&user=${user?.id}`,
		isEnabled: Boolean(tourMatching && user?.id),
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTours(res.data);
			}
		},
	});

	// Complete tour request
	const { fetchRequest: postTours } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/tours`,
		isLazy: true,
		options: {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ name: tourMatching?.name, user: user?.id }),
		},
	});

	const handleEndStep = () => {
		if (!tourActive) {
			return;
		}

		const step = tourActive.steps[stepIndex];

		// Close dialog if step has dialogId
		if (step.dialogId) {
			setSearchParams((prev) => {
				prev.delete('dialog');
				return prev;
			});
		}

		// Move to next step or complete tour if it was the last step
		const nextIndex = stepIndex + 1;

		if (nextIndex < tourActive.steps.length) {
			const nextStep = tourActive.steps[nextIndex];
			setStepIndex(nextIndex);

			if (nextStep.dialogId) {
				setSearchParams((prev) => {
					prev.set('dialog', String(nextStep.dialogId));
					return prev;
				});
			}
		} else {
			if (!tourActive.isImmediate) {
				postTours();
			}

			const step = tourActive.steps[stepIndex];

			if (step.type === 'POPOVER') {
				setTourStatus('CLOSING');
			} else {
				setTourActive(null);
			}

			setStepIndex(0);
		}
	};

	const isTourCompleted = tours.length > 0;

	// Activate tour once fetch resolves
	useEffect(() => {
		// Prevent activating tour if already completed, loading, or another tour is active
		if (!tourMatching || isLoadingTours || isTourCompleted || tourActive) {
			return;
		}

		// Prevent activating the same tour multiple times on route changes
		if (refActivatedTours.current.has(tourMatching.name)) {
			return;
		}

		// Mark tour as activated to prevent re-activation on route changes
		refActivatedTours.current.add(tourMatching.name);
		setTourActive(tourMatching);
		setStepIndex(0);

		// Open dialog for first step if it has a dialogId
		const step = tourMatching.steps[0];

		if (step.dialogId) {
			setSearchParams((prev) => {
				prev.set('dialog', String(step.dialogId));
				return prev;
			});
		}

		// Complete tour immediately if isImmediate
		if (tourMatching.isImmediate) {
			postTours();
		}
	}, [tourActive, tourMatching, isLoadingTours, isTourCompleted, postTours, setSearchParams]);

	if (tourActive || isTourClosing) {
		if (!tourActive) {
			return null;
		}

		// Define current step
		const step = tourActive.steps[stepIndex];

		// Render current step based on its type
		if (step.type === 'DIALOG') {
			return step.render({
				isOpen: paramDialog === step.dialogId,
				onClose: () => handleEndStep(),
			});
		} else if (step.type === 'POPOVER') {
			const anchorEl = step.target ? document.querySelector(`[data-tour="${step.target}"]`) : null;

			if (anchorEl) {
				const { hasOverlay = true, PopoverProps } = step;
				const { actions = [], PopoverProps: MuiPopoverProps } = PopoverProps || {};

				const actionsEnhanced: PopoverProps['actions'] = actions.map((action) => ({
					...action,
					onClick: action.onClick || handleEndStep,
				}));

				return (
					<Popover
						{...PopoverProps}
						actions={actionsEnhanced}
						PopoverProps={{
							...MuiPopoverProps,
							anchorEl: anchorEl as HTMLElement,
							onClose: handleEndStep,
							open: !isTourClosing,
							slotProps: {
								...MuiPopoverProps?.slotProps,
								root: {
									...(MuiPopoverProps?.slotProps?.root as object),
									sx: {
										'& .MuiBackdrop-root': {
											backgroundColor: hasOverlay
												? alpha(theme.palette.common.black, 0.5)
												: 'transparent',
										},
										...(MuiPopoverProps?.slotProps?.root as { sx?: object })?.sx,
									},
								},
							},
							TransitionProps: {
								...MuiPopoverProps?.TransitionProps,
								onExited: (node) => {
									// Call existing onExited if it exists
									MuiPopoverProps?.TransitionProps?.onExited?.(node);

									// End tour after popover exit animation completes
									if (isTourClosing) {
										setTourActive(null);
										setTourStatus('DEFAULT');
									}
								},
							},
						}}>
						{step.render({})}
					</Popover>
				);
			}
		}
	}

	return null;
};
